import { useEffect, useState, useRef } from "react";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";
import { toast } from "react-toastify";
import { IService } from "../Lead.props";
import { useLocation, useNavigate } from "react-router-dom";

const useCampaign = (campaignId?: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Read initial page from URL - always use window.location for reliability
  const getInitialPage = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const page = Number(params.get("page")) || 1;
      console.log("Campaign getInitialPage - URL:", window.location.href, "Page:", page);
      return page;
    }
    // Fallback to React Router location if window is not available (shouldn't happen in browser)
    const params = new URLSearchParams(location.search);
    return Number(params.get("page")) || 1;
  };
  const initialPage = getInitialPage();
  
  const [campaignData, setCampaignData] = useState<any[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(initialPage);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [leadStatusFilter, setLeadStatusFilter] = useState<string>("");
  const pageSize = 10;
  
  const hasInitialLoadRef = useRef<boolean>(false);
  const isUrlSyncRef = useRef<boolean>(false);
  const previousLocationRef = useRef<string>(location.pathname + location.search);
  const justNavigatedBackRef = useRef<boolean>(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState<any | null>(null);
  const [form, setForm] = useState({
    campaignId: campaignId || "",
    leadName: "",
    companyName: "",
    email: "",
    services: "",
    leadStatus: "New",
    companyWebsite: "",
    linkedInLink: "",
    description: "",
    linkedInLeadStatus: "",
    instagramLink: "",
    leadWebLink: "",
    personalization: "",
    follow1: "",
    follow2: "",
    follow3: "",
    aiData: "",
    query: "",
  });

  const [services, setServices] = useState<IService[]>([]);

  const fetchServicesData = async (_event: any, name: string) => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.services}`,
        data: { type: name },
      });
      setServices(result);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServicesData("", "campaign");
  }, []);

  const handleOpenCreate = () => setIsCreateOpen(true);
  const handleCloseCreate = () => {
    setIsCreateOpen(false);
    setForm({
      campaignId: campaignId || "",
      leadName: "",
      companyName: "",
      email: "",
      services: "",
      leadStatus: "New",
      companyWebsite: "",
      linkedInLink: "",
      description: "",
      linkedInLeadStatus: "",
      instagramLink: "",
      leadWebLink: "",
      personalization: "",
      follow1: "",
      follow2: "",
      follow3: "",
      aiData: "",
      query: "",
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Align payload keys with backend expectations (Follow1/Follow2/Follow3)
      const payload: any = { ...form, campaignId, Follow1: form.follow1, Follow2: form.follow2, Follow3: form.follow3 };
      if (!campaignId) {
        toast.warning("Please select a campaign first");
        setIsSubmitting(false);
        return;
      }
      await ApiService({
        method: "POST",
        endpoint: apiPaths.campaignCreate,
        data: payload,
      });
      toast.success("Campaign created");
      handleCloseCreate();
      setPage(1);
      // Update URL to page 1 when creating new campaign
      const params = new URLSearchParams(location.search);
      params.set("page", "1");
      navigate(`?${params.toString()}`, { replace: true });
      fetchCampaignData();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create campaign");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEdit = (campaign: any) => {
    setEditId(campaign?._id || null);
    setForm({
      campaignId:
        (typeof campaign?.campaignId === "object"
          ? campaign?.campaignId?._id
          : campaign?.campaignId) || campaignId || "",
      leadName: campaign?.leadName || "",
      companyName: campaign?.companyName || "",
      email: campaign?.email || "",
      services: campaign?.services || "",
      leadStatus: campaign?.leadStatus || "New",
      companyWebsite: campaign?.companyWebsite || "",
      linkedInLink: campaign?.linkedInLink || "",
      description: campaign?.description || "",
      linkedInLeadStatus: campaign?.linkedInLeadStatus || "",
      instagramLink: campaign?.instagramLink || "",
      leadWebLink: campaign?.leadWebLink || "",
      personalization: campaign?.personalization || "",
      follow1: campaign?.follow1 || campaign?.Follow1 || "",
      follow2: campaign?.follow2 || campaign?.Follow2 || "",
      follow3: campaign?.follow3 || campaign?.Follow3 || "",
      aiData: campaign?.aiData || "",
      query: campaign?.query || "",
    });
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setEditId(null);
  };

  const handleOpenView = (campaign: any) => {
    setViewData({
        ...campaign,
        follow1: campaign?.follow1 || campaign?.Follow1 || "",
        follow2: campaign?.follow2 || campaign?.Follow2 || "",
        follow3: campaign?.follow3 || campaign?.Follow3 || "",
      query: campaign?.query || "",
    });
    setIsViewOpen(true);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setViewData(null);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setIsSubmitting(true);
    try {
      // Align payload keys with backend expectations (Follow1/Follow2/Follow3)
      const payload: any = { ...form, campaignId: form.campaignId || campaignId, Follow1: form.follow1, Follow2: form.follow2, Follow3: form.follow3 };
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.campaignEdit}${editId}`,
        data: payload,
      });
      // If a non-empty query was added/updated, mark leadStatus as "Query"
      if ((form?.query || "").toString().trim().length > 0) {
        try {
          await ApiService({
            method: "PUT",
            endpoint: `${apiPaths.campaignEdit}${editId}`,
            data: { leadStatus: "Query" },
          });
        } catch {}
      }
      toast.success("Campaign updated");
      handleCloseEdit();
      fetchCampaignData();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update campaign");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTableStatusChange = async (id: string, newStatus: string) => {
    // Optimistic update
    setCampaignData((prev) =>
      prev.map((c) => (c._id === id ? { ...c, leadStatus: newStatus } : c))
    );
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.campaignEdit}${id}`,
        data: { leadStatus: newStatus },
      });
      toast.success("Lead status updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update status");
      fetchCampaignData();
    }
  };

  const handleTableLinkedInStatusChange = async (
    id: string,
    newStatus: string
  ) => {
    setCampaignData((prev) =>
      prev.map((c) =>
        c._id === id ? { ...c, linkedInLeadStatus: newStatus } : c
      )
    );
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.campaignEdit}${id}`,
        data: { linkedInLeadStatus: newStatus },
      });
      toast.success("LinkedIn status updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update LinkedIn status");
      fetchCampaignData();
    }
  };

  const handleTableServiceChange = async (id: string, newService: string) => {
    // Optimistic update
    setCampaignData((prev) =>
      prev.map((c) => (c._id === id ? { ...c, services: newService } : c))
    );
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.campaignEdit}${id}`,
        data: { services: newService },
      });
      toast.success("Service updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update service");
      fetchCampaignData();
    }
  };

  const fetchCampaignData = async () => {
    setLoading(true);
    try {
      // If a specific campaign is selected, fetch its list via campaignGetList; otherwise fall back to overall list
      const endpoint = typeof campaignId === "string" && campaignId
        ? `${apiPaths.campaignGetList}?campaignId=${campaignId}&page=1&limit=10000`
        : `${apiPaths.campaignGetAll}?page=1&limit=10000`;

      const response = await ApiService({
        method: "GET",
        endpoint,
      });

      // Support multiple possible response shapes safely
      const sourceList = Array.isArray(response?.campaigns)
        ? response.campaigns
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response)
        ? response
        : [];

      const normalizedCampaigns = (sourceList || []).map((c: any) => ({
        ...c,
        services:
          typeof c?.services === "object" ? c?.services?._id || "" : c?.services || "",
        follow1: c?.follow1 ?? c?.Follow1 ?? "",
        follow2: c?.follow2 ?? c?.Follow2 ?? "",
        follow3: c?.follow3 ?? c?.Follow3 ?? "",
      }));
      setAllCampaigns(normalizedCampaigns);
      // If the API also returns services list, prefer it
      if (Array.isArray(response?.service)) {
        setServices(response.service);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  const recomputePageData = () => {
    const term = (search || "").trim().toLowerCase();
    const filteredByCampaign = campaignId
      ? allCampaigns.filter((c: any) => {
          const cId = typeof c?.campaignId === "object" ? c?.campaignId?._id : c?.campaignId;
          return cId === campaignId;
        })
      : allCampaigns;

    // Show all campaigns regardless of role
    const filteredByOwner = filteredByCampaign;
    // Apply lead status filter first if selected
    const filteredByStatus = leadStatusFilter
      ? filteredByOwner.filter((c: any) => {
          const status = (c?.leadStatus || "").toString().toLowerCase();
          return status === leadStatusFilter.toLowerCase();
        })
      : filteredByOwner;

    const filtered = term
      ? filteredByStatus.filter((c: any) => {
          const fields = [
            c?.leadName,
            c?.companyName,
            c?.email,
            c?.linkedInLeadStatus,
            c?.leadStatus,
            c?.description,
            c?.companyWebsite,
            c?.linkedInLink,
            c?.leadWebLink,
            c?.personalization,
            c?.Follow1 || c?.follow1,
            c?.Follow2 || c?.follow2,
          ]
            .filter(Boolean)
            .map((v: any) => String(v).toLowerCase());
          return fields.some((v: string) => v.includes(term));
        })
      : filteredByStatus;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    setCampaignData(filtered.slice(start, end));
    setTotalItems(filtered.length);
    setTotalPage(Math.max(1, Math.ceil(filtered.length / pageSize)));
  };

  useEffect(() => {
    fetchCampaignData();
  }, []);

  // Initial load on mount: Always read page from URL as source of truth
  useEffect(() => {
    // Always read from window.location to ensure we get the current URL
    const currentSearch = window.location.search;
    const params = new URLSearchParams(currentSearch);
    const urlPage = Number(params.get("page")) || 1;
    
    console.log("Campaign initial load - URL:", window.location.href, "Page from URL:", urlPage, "Current page state:", page);
    
    // Always set page state to match URL - force update to ensure we use URL as source of truth
    // This is critical when component remounts after navigating back from schedule
    setPage(urlPage);
    hasInitialLoadRef.current = true;
    previousLocationRef.current = window.location.pathname + window.location.search;
    
    // Set a flag to prevent filter effect from resetting immediately after initial load
    isUrlSyncRef.current = true;
    setTimeout(() => {
      isUrlSyncRef.current = false;
    }, 1000); // Give enough time for all effects to settle
  }, []);

  // Sync page state with URL when location changes (handles returning from other routes)
  useEffect(() => {
    const currentSearch = window.location.search;
    const currentPathname = window.location.pathname;
    const currentLocation = currentPathname + currentSearch;
    
    // Skip on initial mount (handled by initial load effect above)
    if (!hasInitialLoadRef.current) {
      previousLocationRef.current = currentLocation;
      return;
    }
    
    const previousLocation = previousLocationRef.current;
    const locationChanged = previousLocation !== currentLocation;
    
    // Only handle if we're on the campaign-list route
    if (currentPathname.includes("/campaign-list")) {
      const params = new URLSearchParams(currentSearch);
      const urlPage = Number(params.get("page")) || 1;
      
      // Check if pathname changed (coming from different route like campaign-schedule)
      const previousPathname = previousLocation.split("?")[0];
      const pathnameChanged = !previousPathname.includes("/campaign-list");
      
      // Always sync page when:
      // 1. Location changed AND pathname changed (returning from other route) - MOST IMPORTANT
      // 2. OR location changed AND page in URL differs from current state
      // This ensures returning from schedule page always loads the correct page
      if (locationChanged && (pathnameChanged || urlPage !== page)) {
        console.log("Campaign URL sync - Current URL:", window.location.href, "Page from URL:", urlPage, "Current page state:", page, "pathnameChanged:", pathnameChanged, "locationChanged:", locationChanged);
        isUrlSyncRef.current = true;
        setPage(urlPage);
        // Keep isUrlSyncRef true longer to prevent filter effect from resetting
        setTimeout(() => {
          isUrlSyncRef.current = false;
        }, 500); // Increased timeout to prevent race condition
      } else if (pathnameChanged && urlPage !== page) {
        // Extra safety: if pathname changed (returning from other route), always sync page
        // This handles edge cases where locationChanged might be false but we're still returning
        console.log("Campaign URL sync (pathname change) - Current URL:", window.location.href, "Page from URL:", urlPage, "Current page state:", page);
        isUrlSyncRef.current = true;
        setPage(urlPage);
        // Keep isUrlSyncRef true longer to prevent filter effect from resetting
        setTimeout(() => {
          isUrlSyncRef.current = false;
        }, 500); // Increased timeout to prevent race condition
      }
    }
    
    // Update ref after processing
    previousLocationRef.current = currentLocation;
  }, [location.pathname, location.search, page]);

  useEffect(() => {
    recomputePageData();
  }, [page, search, leadStatusFilter, allCampaigns, campaignId]);

  // Update URL when page changes (and it's not triggered by URL sync)
  useEffect(() => {
    if (isUrlSyncRef.current || !hasInitialLoadRef.current) {
      return;
    }
    // Check if URL already has the correct page - if so, don't update
    const currentSearch = window.location.search;
    const currentParams = new URLSearchParams(currentSearch);
    const urlPage = Number(currentParams.get("page")) || 1;
    
    // Only update URL if page state differs from URL
    if (urlPage !== page) {
      console.log("Campaign page change - Updating URL from page state:", page, "URL had:", urlPage);
      // Immediately update URL to reflect page change
      const params = new URLSearchParams(location.search);
      params.set("page", page.toString());
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [page]);

  // Reset to first page when filter changes
  useEffect(() => {
    // Only reset page to 1 if initial load has completed (skip on initial mount)
    if (!hasInitialLoadRef.current) {
      console.log("Campaign filter change - Skipping reset because initial load not complete");
      return;
    }
    
    // ALWAYS check URL first - if URL has a page > 1, NEVER reset to page 1
    // This is the most important safeguard to prevent resetting when returning from schedule
    const currentSearch = window.location.search;
    const currentParams = new URLSearchParams(currentSearch);
    const urlPage = Number(currentParams.get("page")) || 1;
    
    if (urlPage > 1) {
      console.log("Campaign filter change - URL has page > 1 (", urlPage, "), PRESERVING it. Not resetting to page 1.");
      // Don't reset page, just update the filter
      // This is critical when returning from schedule page
      return;
    }
    
    // Don't reset if we're in the middle of a URL sync (returning from another route)
    if (isUrlSyncRef.current) {
      console.log("Campaign filter change - Skipping reset because URL sync in progress");
      return;
    }
    
    // Only reset to page 1 if filter changes AND URL has page=1 or no page
    console.log("Campaign filter change - Resetting to page 1 (URL has page 1 or no page)");
    setPage(1);
    // Update URL to page 1 when filter changes
    const params = new URLSearchParams(location.search);
    params.set("page", "1");
    navigate(`?${params.toString()}`, { replace: true });
  }, [leadStatusFilter]);

  return {
    campaignData,
    loading,
    error,
    page,
    search,
    leadStatusFilter,
    totalPage,
    totalItems,
    pageSize,
    isCreateOpen,
    isSubmitting,
    isEditOpen,
    isViewOpen,
    setPage,
    setForm,
    setSearch,
    setLeadStatusFilter,
    fetchCampaignData,
    handleOpenCreate,
    handleCloseCreate,
    handleOpenEdit,
    handleCloseEdit,
    handleOpenView,
    handleCloseView,
    form,
    handleChange,
    handleCreate,
    handleUpdate,
    services,
    viewData,
    handleTableStatusChange,
    handleTableLinkedInStatusChange,
    handleTableServiceChange,
  };
};

export default useCampaign;
